import { api } from '../../../../../lib';
import { DataItem } from '../../../../../@types/DataItem';
import type { CategoryLevel } from '../../../../../reducers/gerenciadorPrecos/segmentos';
import {
  CadastrosContractUser,
  PricerAssignment,
  PricerAssignmentPayload,
  PricerAssignmentsPage,
  PricerAssignmentsParams,
  PricingScopeLevel,
  PricingScopeLevelUpdate,
  PricingScopeUser,
} from './types';

const PRICING_SCOPE_URL = '/setup/pricing-scope';

const getClientId = () => JSON.parse(localStorage.getItem('cliente') ?? '{}')?.codigo;

const hasPricingScopeLevel = (value: unknown): value is PricingScopeLevel =>
  typeof value === 'object' && value !== null && typeof (value as PricingScopeLevel).level === 'number';

export async function getPricingScopeLevel(): Promise<PricingScopeLevel | null> {
  try {
    const response = await api.get<PricingScopeLevel>(`${PRICING_SCOPE_URL}/level`, {
      expectToken: true,
      validateStatus: status => status === 200 || status === 204 || status === 404,
    });

    return response.status === 204 || response.status === 404 || !hasPricingScopeLevel(response.data) ? null : response.data;
  } catch (error) {
    if (error?.response?.status === 404) return null;
    throw error;
  }
}

export async function createPricingScopeLevel(level: number): Promise<PricingScopeLevel | undefined> {
  const response = await api.post<PricingScopeLevel>(`${PRICING_SCOPE_URL}/level`, { level }, { expectToken: true });
  return hasPricingScopeLevel(response.data) ? response.data : undefined;
}

export async function updatePricingScopeLevel(level: number): Promise<PricingScopeLevelUpdate | undefined> {
  const response = await api.put<PricingScopeLevelUpdate>(`${PRICING_SCOPE_URL}/level`, { level }, { expectToken: true });
  return hasPricingScopeLevel(response.data) ? response.data : undefined;
}

export async function getPricerAssignments(params: PricerAssignmentsParams): Promise<PricerAssignmentsPage> {
  const response = await api.get<PricerAssignmentsPage>(`${PRICING_SCOPE_URL}/assignments`, {
    expectToken: true,
    params,
  });
  return response.data;
}

export async function getPricerAssignmentsForFilter(search: string): Promise<PricerAssignment[]> {
  const firstPage = await getPricerAssignments({ search, page: 0, size: 20, sort: 'userEmail,asc' });
  const assignments = [...firstPage.content];
  const totalPages = firstPage.totalPages || 1;

  for (let page = 1; page < totalPages; page += 1) {
    const response = await getPricerAssignments({ search, page, size: 20, sort: 'userEmail,asc' });
    assignments.push(...response.content);
  }

  return assignments;
}

type ResponsibleFilterMetadata = {
  categoryLevel?: CategoryLevel;
  injectedCategories?: string[];
};

export type ResponsibleFilterOption = DataItem<'name' | 'email', string> & ResponsibleFilterMetadata;

export function toResponsibleOption(assignment: PricerAssignment): ResponsibleFilterOption {
  const name = assignment.userName.trim();

  return {
    label: name,
    value: assignment.id,
    name,
    email: assignment.userEmail,
  };
}

export async function getPricerAssignment(id: string): Promise<PricerAssignment> {
  const response = await api.get<PricerAssignment>(`${PRICING_SCOPE_URL}/assignments/${id}`, { expectToken: true });
  return response.data;
}

export async function getAssignedPricingUsers(): Promise<number[]> {
  const response = await api.get<number[]>(`${PRICING_SCOPE_URL}/assigned-users`, { expectToken: true });
  return response.data;
}

export async function createPricerAssignment(payload: PricerAssignmentPayload): Promise<PricerAssignment> {
  const response = await api.post<PricerAssignment>(`${PRICING_SCOPE_URL}/assignments`, payload, { expectToken: true });
  return response.data;
}

export async function updatePricerAssignment({ id, payload }: { id: string; payload: PricerAssignmentPayload }): Promise<PricerAssignment> {
  const response = await api.put<PricerAssignment>(`${PRICING_SCOPE_URL}/assignments/${id}`, payload, { expectToken: true });
  return response.data;
}

export async function deletePricerAssignment(id: string): Promise<void> {
  await api.delete(`${PRICING_SCOPE_URL}/assignments/${id}`, { expectToken: true });
}

export async function getPricingScopeCategories(level: number, query = ''): Promise<string[]> {
  const response = await api.post<string[]>(
    '/products/filters',
    { field: `categoryLevel${level}`, ...(query ? { query } : {}) },
    {
      expectToken: true,
      params: {
        clientId: getClientId(),
        size: 20,
      },
    },
  );
  return response.data;
}

export async function getActiveIpaUsers(): Promise<PricingScopeUser[]> {
  const response = await api.get<CadastrosContractUser[]>(`${process.env.REACT_APP_CADASTROS_API_URL}/contratos/usuarioServicos`, {
    expectToken: true,
    params: {
      cliente: getClientId(),
      query: '',
    },
  });

  return response.data
    .map(item => ({
      cdUsuario: item.usuario.codigo,
      name: item.usuario.nome,
      email: item.usuario.login,
    }))
    .sort(
      (left, right) =>
        left.name.localeCompare(right.name, 'pt-BR', { sensitivity: 'base' }) ||
        left.email.localeCompare(right.email, 'pt-BR', { sensitivity: 'base' }) ||
        left.cdUsuario - right.cdUsuario,
    );
}
